/**
 * Service de Planos de Cobranca (Campanhas)
 *
 * CRUD contra /v1/admin/charge-plans
 */

const API_BASE =
  (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";

export type ChargePlanTipoValor = "fixo" | "escolhido_cliente";
export type ChargePlanStatus = "ativo" | "inativo" | "rascunho";
export type ChargePlanTipoMulta =
  | "parcela_extra"
  | "percentual"
  | "valor_fixo"
  | "nenhuma";
export type ChargePlanRenovacaoIncrementoTipo =
  | "fixo"
  | "percentual"
  | "nenhum";
export type ChargePlanInstallmentTipo =
  | "diario"
  | "semanal"
  | "quinzenal"
  | "mensal"
  | "personalizado";

export interface ChargePlanInstallment {
  id?: string;
  tipo: ChargePlanInstallmentTipo;
  label?: string | null;
  taxaPct: string;
  // numParcelas = MAX da faixa; numParcelasMin = piso. Cliente escolhe na faixa.
  numParcelas: number;
  numParcelasMin?: number;
  intervaloDias?: number | null;
  ordem?: number;
  ativo?: boolean;
}

export interface ChargePlanNotificacaoSlot {
  ativo: boolean;
  texto: string;
}

export interface ChargePlanNotificacoesConfig {
  organizacao?: ChargePlanNotificacaoSlot;
  parcela?: ChargePlanNotificacaoSlot;
  ultimo?: ChargePlanNotificacaoSlot;
}

export type ChargePlanDocumentoTipo =
  | "ccb"
  | "contrato"
  | "nota_promissoria_digital";

export interface ChargePlanDocumento {
  tipo: ChargePlanDocumentoTipo;
  incluir: boolean;
  nomeInterno?: string;
  tituloCliente?: string;
  mensagem?: string;
  observacao?: string;
  arquivoStorageKey?: string | null;
  /** HTML template com placeholders {{NOME}}, {{CPF}} etc. — renderizado no envio. */
  templateHtml?: string | null;
}

export interface ChargePlanDocumentosConfig {
  documentos: ChargePlanDocumento[];
}

/**
 * Step 1 do wizard: "Faca seu cliente cadastrar online direto pelo app".
 * Espelha o tipo no dominio da API.
 */
export interface DadosPessoaisConfig {
  habilitado: boolean;
  cpfNomeDn: boolean;
  cep: boolean;
  enderecoCompleto: boolean;
}

export interface ContatosConfig {
  habilitado: boolean;
  whatsapp: boolean;
  email: boolean;
}

export interface ReferenciasConfig {
  habilitado: boolean;
  quantidade: number;
}

export interface DocumentoObrigatorioItem {
  id: string;
  nome: string;
  label: string;
  obrigatorio?: boolean;
}

export interface DocumentosObrigatoriosConfig {
  habilitado: boolean;
  lista: DocumentoObrigatorioItem[];
}

export interface FotoVideoItem {
  id: string;
  nome: string;
  exemplo: string;
  quantidade: number;
  ordem: number;
  tipo: "foto" | "video" | "selfie";
  obrigatorio?: boolean;
}

export interface FotosVideosConfig {
  habilitado: boolean;
  lista: FotoVideoItem[];
}

export interface ChavePixConfig {
  habilitado: boolean;
}

export interface OnboardingConfig {
  modeloPronto: boolean;
  dadosPessoais: DadosPessoaisConfig;
  contatos: ContatosConfig;
  referencias: ReferenciasConfig;
  documentos: DocumentosObrigatoriosConfig;
  fotosVideos: FotosVideosConfig;
  chavePix: ChavePixConfig;
  // Quando modeloPronto=false, armazena os passos do fluxo customizado
  // criados pelo cliente via FlowEditor.
  customFlowSteps?: unknown[];
}

export interface ChargePlan {
  id: string;
  tenantId: string;
  codigo: string;
  nome: string;
  tipoValor: ChargePlanTipoValor;
  valorFixo?: string | null;
  valorMinimo?: string | null;
  valorMaximo?: string | null;
  valorStep?: string | null;
  maxActiveCharges?: number;
  renovacaoHabilitada: boolean;
  renovacaoIncrementoTipo: ChargePlanRenovacaoIncrementoTipo;
  renovacaoIncrementoValor?: string | null;
  renovacaoParcelasMinPagas?: number | null;
  /** % minimo do contrato (em R$) pago para liberar renovacao.
   * Alternativa ao gate por parcelas — apenas UM dos dois eh usado. */
  renovacaoPercentualPagoMin?: string | null;
  renovacaoDiasMin?: number | null;
  renovacaoMultasMax?: number | null;
  renovacaoMultasAbertasMax?: number | null;
  diasSemana: number[];
  incluirFeriados: boolean;
  tipoMulta: ChargePlanTipoMulta;
  multaPercentual?: string | null;
  multaValorFixo?: string | null;
  multaAplicarSobre?: string | null;
  multaExplicacao?: string | null;
  mensagemCliente?: string | null;
  notificacoesConfig?: ChargePlanNotificacoesConfig | null;
  documentosConfig?: ChargePlanDocumentosConfig | null;
  validacaoParceiroHabilitada?: boolean;
  onboardingConfig?: OnboardingConfig;
  status: ChargePlanStatus;
  installments: ChargePlanInstallment[];
  /** Quantidade de sub-planos ativos (preenchido apenas em listagens). */
  installmentsCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChargePlanInput
  extends Omit<
    ChargePlan,
    "id" | "tenantId" | "createdAt" | "updatedAt" | "installments"
  > {
  installments: ChargePlanInstallment[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: { page: number; limit: number; total: number; totalPages: number };
  error?: { code: string; message: string };
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem("payflow_token");
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!res.ok || !json.success) {
    throw new Error(json.error?.message || `HTTP ${res.status}`);
  }
  return json.data;
}

export const chargePlansService = {
  async list(filter: {
    status?: ChargePlanStatus;
    search?: string;
    page?: number;
    limit?: number;
  } = {}) {
    const params = new URLSearchParams();
    if (filter.status) params.set("status", filter.status);
    if (filter.search) params.set("search", filter.search);
    if (filter.page) params.set("page", String(filter.page));
    if (filter.limit) params.set("limit", String(filter.limit));
    const res = await fetch(
      `${API_BASE}/v1/admin/charge-plans?${params.toString()}`,
      { headers: authHeaders() },
    );
    const json = (await res.json()) as ApiResponse<ChargePlan[]>;
    if (!res.ok || !json.success) {
      throw new Error(json.error?.message || `HTTP ${res.status}`);
    }
    return { items: json.data, meta: json.meta };
  },

  get(id: string) {
    return request<ChargePlan>(`/v1/admin/charge-plans/${id}`);
  },

  create(input: ChargePlanInput) {
    return request<ChargePlan>(`/v1/admin/charge-plans`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update(id: string, input: Partial<ChargePlanInput>) {
    return request<ChargePlan>(`/v1/admin/charge-plans/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  remove(id: string) {
    return request<null>(`/v1/admin/charge-plans/${id}`, { method: "DELETE" });
  },
};
